import React, { useState } from "react";
import { CircularProgress, Container } from "@mui/material";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import AppLayout from "../../components/AppLayout/AppLayout";
import ButtonNative from "../../components/StyledComponents/ButtonNative";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import * as pdfjsLib from "pdfjs-dist";
import { saveAs } from "file-saver";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.js`;

const OCRScanner = () => {
  const [pages, setPages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [aiScanning, setAiScanning] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");

  const generateThumbnailsFromPDF = async (file, fileId) => {
    const buffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument(buffer).promise;
    const thumbnails = [];

    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const scale = 300 / viewport.width;
      const scaledViewport = page.getViewport({ scale });

      const canvas = document.createElement("canvas");
      canvas.height = scaledViewport.height;
      canvas.width = scaledViewport.width;
      const context = canvas.getContext("2d");
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      await page.render({
        canvasContext: context,
        viewport: scaledViewport,
      }).promise;

      thumbnails.push({
        id: `${fileId}-${i}`,
        thumbnail: canvas.toDataURL("image/png", 1.0),
        pageNum: i - 1,
        fileType: "pdf",
        fileName: file.name,
        file,
      });
    }
    return thumbnails;
  };

  const generateThumbnailsFromImage = async (file, fileId) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;

    await new Promise((resolve) => (img.onload = resolve));

    const canvas = document.createElement("canvas");
    const scale = 300 / img.width;
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    const context = canvas.getContext("2d");
    context.imageSmoothingEnabled = true;
    context.imageSmoothingQuality = "high";
    context.drawImage(img, 0, 0, canvas.width, canvas.height);

    const thumbnail = canvas.toDataURL("image/png", 1.0);
    URL.revokeObjectURL(url);

    return [
      {
        id: `${fileId}-1`,
        thumbnail,
        pageNum: 0,
        fileType: "image",
        fileName: file.name,
        file,
      },
    ];
  };

  const handleFileUpload = async (event) => {
    setUploading(true);
    const newFiles = Array.from(event.target.files);

    try {
      const newPages = [];
      for (let i = 0; i < newFiles.length; i++) {
        const file = newFiles[i];
        const fileId = `${Date.now()}-${i}-${Math.random().toString(36).substr(2, 9)}`;
        if (file.type === "application/pdf") {
          const pdfPages = await generateThumbnailsFromPDF(file, fileId);
          newPages.push(...pdfPages);
        } else if (file.type.startsWith("image/")) {
          const imagePages = await generateThumbnailsFromImage(file, fileId);
          newPages.push(...imagePages);
        }
      }

      setPages((prevPages) => [...prevPages, ...newPages]);
    } catch (error) {
      console.error("Error processing files:", error);
    } finally {
      setUploading(false);
    }
  };

  const handlePageDelete = (pageId) => {
    setPages((prev) => prev.filter((page) => page.id !== pageId));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedPages = Array.from(pages);
    const [movedPage] = reorderedPages.splice(result.source.index, 1);
    reorderedPages.splice(result.destination.index, 0, movedPage);

    setPages(reorderedPages);
  };

  const handleAIScan = async () => {
    if (pages.length === 0) return;

    setAiScanning(true);
    setHtmlContent("");
    let accumulatedContent = "";

    try {
      for (let pageIndex = 0; pageIndex < pages.length; pageIndex++) {
        const page = pages[pageIndex];
        let imageData;
        if (page.fileType === "pdf") {
          const images = await convertPDFToImages(page.file);
          imageData = images[page.pageNum];
        } else {
          imageData = await fileToBase64(page.file);
        }

        const prompt = `
          Extract the text from this image.
          Return the extracted text as HTML (not markdown). Use proper HTML tags for formatting:
          - Use <h1>, <h2>, etc., for headings.
          - Use <p> for paragraphs.
          - Use <table>, <tr>, <th>, <td> for tables.
          - Use <b> for bold text, <i> for italic text.
          - Use <ul> and <li> for unordered lists, <ol> and <li> for ordered lists.
          - Use <p align="center"> to center text where if only in the document that text is centered, don't do it unnecessarily.
          - Do not include any <script> tags or other executable code.
          - Absolutely do not wrap the output in code blocks (e.g., \`\`\`html ... \`\`\` or \`\`\` ... \`\`\`). Return the raw HTML content directly.
          For receipt-like content, format the items as an HTML table with columns for Quantity, Item, and Price.
          Ensure all HTML tags are properly closed and nested.
        `;

        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
        const payload = {
          contents: [{
            parts: [
              { "text": prompt },
              { "inline_data": { "mime_type": "image/png", "data": imageData } }
            ]
          }]
        };

        const response = await fetch(`${backendUrl}/proxy/gemini/generate-content`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let pageContent = "";

        accumulatedContent += `<div class="page-container" id="page-${pageIndex}">`;

        while (true) {
          const { value, done } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n');

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.substring(6);
                const data = JSON.parse(jsonStr);
                const delta = data.candidates?.[0]?.content?.parts?.[0]?.text;
                if (delta) {
                  pageContent += delta;
                  setHtmlContent(accumulatedContent + pageContent + '</div>');
                }
              } catch (e) {
              }
            }
          }
        }
        accumulatedContent += pageContent + '</div>';
      }
    } catch (error) {
      console.error("Error during AI OCR:", error);
      setHtmlContent(accumulatedContent + `<div class="page-container"><p><b>Error:</b> ${error.message}</p></div>`);
    } finally {
      setAiScanning(false);
    }
  };

  const convertPDFToImages = async (file) => {
    const buffer = await file.arrayBuffer();
    const pdfDoc = await pdfjsLib.getDocument(buffer).promise;
    const images = [];

    for (let i = 1; i <= pdfDoc.numPages; i++) {
      const page = await pdfDoc.getPage(i);
      const viewport = page.getViewport({ scale: 1 });
      const canvas = document.createElement("canvas");
      canvas.height = viewport.height;
      canvas.width = viewport.width;
      const context = canvas.getContext("2d");

      await page.render({
        canvasContext: context,
        viewport: viewport,
      }).promise;

      const base64 = canvas.toDataURL("image/png").split(",")[1];
      images.push(base64);
    }
    return images;
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

  const handleClear = () => {
    setPages([]);
    setHtmlContent("");
  };

  const handleExport = async (format) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/ocrscanner/export/${format}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content: htmlContent }),
      });

      const blob = await response.blob();
      saveAs(blob, `ocr_output.${format}`);
    } catch (error) {
      console.error(`Error exporting to ${format.toUpperCase()}:`, error);
    }
  };

  return (
    <AppLayout>
      <Container maxWidth="xl" sx={{ textAlign: "center", padding: "40px" }}>
        <Card
          sx={{ padding: "40px", marginBottom: "40px"  }}
          header={
            <CardHeader
              avatar={<Icon name="document" />}
              subtitleText="Upload, Arrange, and OCR Scan Documents"
              titleText="OCR Scanner"
            />
          }
        >
          <div
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "20px",
              justifyContent: "center",
            }}
          >
            <ButtonNative
              component="label"
              disabled={uploading || scanning || aiScanning}
              sx={{ padding: "10px 20px" }}
            >
              {uploading ? <CircularProgress size={24} /> : "Choose Files"}
              <input
                type="file"
                multiple
                accept="application/pdf,image/*"
                onChange={handleFileUpload}
                hidden
              />
            </ButtonNative>
            <ButtonNative
              onClick={handleAIScan}
              disabled={uploading || scanning || aiScanning || pages.length === 0}
              sx={{ padding: "10px 20px" }}
            >
              {aiScanning ? <CircularProgress size={24} /> : "AI Scan"}
            </ButtonNative>
            <ButtonNative
              onClick={handleClear}
              disabled={uploading || scanning || aiScanning}
              sx={{ padding: "10px 20px" }}
            >
              Clear
            </ButtonNative>
            <ButtonNative
              onClick={() => handleExport("docx")}
              disabled={uploading || scanning || aiScanning || !htmlContent}
              sx={{ padding: "10px 20px" }}
            >
              Export to DOCX
            </ButtonNative>
            <ButtonNative
              onClick={() => handleExport("pdf")}
              disabled={uploading || scanning || aiScanning || !htmlContent}
              sx={{ padding: "10px 20px" }}
            >
              Export to PDF
            </ButtonNative>
          </div>

          <div style={{ display: "flex", gap: "20px", height: "600px" }}>
            <div
              style={{
                flex: 1,
                overflowY: "auto",
                overflowX: "hidden",
                maxHeight: "600px",
                display: "flex",
                justifyContent: "center",
              }}
            >
              {pages.length > 0 && (
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="pages">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "20px",
                          padding: "20px",
                          width: "fit-content",
                          maxWidth: "100%",
                        }}
                      >
                        {pages.map((page, index) => (
                          <Draggable key={page.id} draggableId={page.id} index={index}>
                            {(provided) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={{
                                  width: "220px",
                                  border: "1px solid #eee",
                                  padding: "15px",
                                  background: "#f9f9f9",
                                  cursor: "move",
                                  boxSizing: "border-box",
                                  ...provided.draggableProps.style,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    alignItems: "center",
                                    marginBottom: "10px",
                                  }}
                                >
                                  <button
                                    onClick={() => handlePageDelete(page.id)}
                                    style={{
                                      background: "red",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "50%",
                                      width: "20px",
                                      height: "20px",
                                      cursor: "pointer",
                                    }}
                                  >
                                    ×
                                  </button>
                                </div>
                                <img
                                  src={page.thumbnail}
                                  alt={`Page ${page.pageNum + 1}`}
                                  style={{ width: "100%", height: "auto" }}
                                />
                                <div style={{ textAlign: "center" }}>
                                  <small>
                                    Page {page.pageNum + 1} ({page.fileName})
                                  </small>
                                </div>
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>

            <div
              style={{
                flex: 2,
                border: "1px solid #ccc",
                padding: "10px",
                overflowY: "auto",
                maxHeight: "600px",
                minHeight: "600px",
                position: "relative",
                textAlign: "left",
                backgroundColor: "#f9f9f9"
              }}
              dangerouslySetInnerHTML={{ __html: `
                <style>
                  .page-container {
                    border: 1px solid #ddd;
                    padding: 15px;
                    margin-bottom: 20px;
                    background-color: #fff;
                    border-radius: 5px;
                  }
                </style>
                ${htmlContent}
              ` }}
            />
          </div>
        </Card>
      </Container>
    </AppLayout>
  );
};

export default OCRScanner;